// src/pages/BetaTerms.tsx
// Términos y Condiciones de Servicio — versión comercial.
// Conforme a la Ley N° 29733 y legislación peruana aplicable.

import { CreditCard, Database, BookOpen, Scale, Power, AlertTriangle, ArrowLeft, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CONTACT } from '../constants/config';
import { useTheme } from '../context/ThemeContext';
import { colors } from '../utils/theme';
import { useLang } from '../context/LangContext';

const APP_NAME = import.meta.env.VITE_APP_NAME ?? 'TryQuiPus';
const LEGAL_NAME = 'Quipus Tech LLC';

const SECTION_TITLE = 'text-base font-semibold mb-3 flex items-center gap-2';
const BODY = 't-small leading-relaxed';

export default function BetaTerms() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const c = colors(isDark);
  const navigate = useNavigate();
  const { lang, setLang } = useLang();
  const isEs = lang === 'es';

  const cardStyle: React.CSSProperties = {
    background: c.card,
    border: `1px solid ${c.border}`,
  };

  return (
    <div className="min-h-screen px-4 py-10" style={{ background: c.canvas }}>
      {/* ES | EN toggle */}
      <div className="fixed top-4 right-4 flex items-center gap-1 rounded-full shadow px-1 py-1"
        style={{ background: c.card, border: `1px solid ${c.border}` }}>
        <button
          onClick={() => setLang('es')}
          className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
          style={isEs ? { background: 'var(--brand)', color: 'var(--on-brand)' } : { color: c.textSub }}
        >
          ES
        </button>
        <button
          onClick={() => setLang('en')}
          className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
          style={!isEs ? { background: 'var(--brand)', color: 'var(--on-brand)' } : { color: c.textSub }}
        >
          EN
        </button>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">

        {/* Back */}
        <button
          onClick={() => navigate('/login')}
          className="flex items-center gap-2 text-sm transition-colors"
          style={{ color: c.textSub }}
        >
          <ArrowLeft size={15} />
          {isEs ? 'Volver' : 'Back'}
        </button>

        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-[2rem] bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center shrink-0 mt-0.5">
            <FileText size={20} className="text-cyan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: c.textPrimary }}>
              {isEs ? 'Términos y Condiciones de Servicio' : 'Terms and Conditions of Service'}
            </h1>
            <p className="text-sm mt-1" style={{ color: c.textSub }}>
              {APP_NAME}™ — {isEs ? 'Versión 1.0 · Vigente desde mayo 2026' : 'Version 1.0 · Effective since May 2026'}
            </p>
            <p className="t-2xs mt-1" style={{ color: c.textMuted }}>
              {isEs
                ? `Al usar ${APP_NAME}™ aceptas estos Términos en su totalidad. Si no los aceptas, no uses el servicio.`
                : `By using ${APP_NAME}™ you accept these Terms in their entirety. If you do not accept them, do not use the service.`}
            </p>
          </div>
        </div>

        {/* 1 — Suscripción y Pago / Subscription and Payment */}
        <div className="rounded-2xl p-6 space-y-4" style={cardStyle}>
          <h2 className={SECTION_TITLE} style={{ color: c.textPrimary }}>
            <CreditCard size={15} className="text-cyan-400 shrink-0" />
            {isEs ? '1. Suscripción y Pago' : '1. Subscription and Payment'}
          </h2>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? `${APP_NAME}™ opera bajo un modelo de suscripción mensual. El procesamiento de pagos estará disponible próximamente. Los planes disponibles son:`
              : `${APP_NAME}™ operates on a monthly subscription model. Payment processing will be available soon. The available plans are:`}
          </p>

          {/* Planes */}
          <div className="grid grid-cols-3 gap-3">
            {(isEs
              ? [
                  { name: 'Starter',      desc: 'Hasta 1 empresa · funcionalidades esenciales' },
                  { name: 'Professional', desc: 'Hasta 5 empresas · reportes avanzados' },
                  { name: 'Business',     desc: 'Empresas ilimitadas · soporte prioritario' },
                ]
              : [
                  { name: 'Starter',      desc: 'Up to 1 company · essential features' },
                  { name: 'Professional', desc: 'Up to 5 companies · advanced reports' },
                  { name: 'Business',     desc: 'Unlimited companies · priority support' },
                ]
            ).map(({ name, desc }) => (
              <div key={name} className="rounded-xl p-3" style={{ background: c.subtleBg, border: `1px solid ${c.border}` }}>
                <p className="t-sm font-semibold mb-1" style={{ color: c.info }}>{name}</p>
                <p className="t-xs leading-relaxed mb-1" style={{ color: c.textSub }}>
                  {isEs
                    ? 'según los precios vigentes publicados en la plataforma al momento de la suscripción'
                    : 'at the current prices published on the platform at the time of subscription'}
                </p>
                <p className="t-xs leading-relaxed" style={{ color: c.textSub }}>{desc}</p>
              </div>
            ))}
          </div>

          <ul className="t-small leading-loose list-none space-y-1 pl-1" style={{ color: c.textNormal }}>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? 'El cobro se realiza automáticamente cada 30 días a partir de la fecha de suscripción.'
                : 'Billing occurs automatically every 30 days from the subscription date.'}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              <strong style={{ color: c.textPrimary }}>
                {isEs ? 'Sin reembolsos' : 'No refunds'}
              </strong>{' '}
              {isEs
                ? <>una vez transcurridos 5 días hábiles desde el cobro. Dentro de los primeros 5 días hábiles puedes solicitar reembolso escribiendo a{' '}<a href={`mailto:${CONTACT.EMAIL_SUPPORT}`} className="text-cyan-400 hover:underline">{CONTACT.EMAIL_SUPPORT}</a>.</>
                : <>after 5 business days from billing. Within the first 5 business days you may request a refund by writing to{' '}<a href={`mailto:${CONTACT.EMAIL_SUPPORT}`} className="text-cyan-400 hover:underline">{CONTACT.EMAIL_SUPPORT}</a>.</>}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? 'La cancelación es efectiva al término del período de facturación vigente. El acceso continúa hasta esa fecha.'
                : 'Cancellation takes effect at the end of the current billing period. Access continues until that date.'}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? 'Los precios no incluyen impuestos adicionales que pudieran ser aplicables según la normativa tributaria peruana vigente.'
                : 'Prices do not include additional taxes that may apply under current Peruvian tax regulations.'}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? `${LEGAL_NAME} se reserva el derecho de modificar precios con un aviso mínimo de 30 días antes de la renovación siguiente.`
                : `${LEGAL_NAME} reserves the right to modify prices with a minimum 30-day notice before the next renewal.`}
            </li>
          </ul>
        </div>

        {/* 2 — Responsabilidad de Datos / Data Responsibility */}
        <div className="rounded-2xl p-6 space-y-3" style={cardStyle}>
          <h2 className={SECTION_TITLE} style={{ color: c.textPrimary }}>
            <Database size={15} className="text-cyan-400 shrink-0" />
            {isEs ? '2. Responsabilidad de Datos' : '2. Data Responsibility'}
          </h2>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? <>En el marco de la <strong style={{ color: c.textPrimary }}>Ley N° 29733 — Ley de Protección de Datos Personales del Perú</strong> y su Reglamento (D.S. N° 003-2013-JUS):</>
              : <>Under the framework of <strong style={{ color: c.textPrimary }}>Law N° 29733 — Personal Data Protection Law of Peru</strong> and its Regulations (D.S. N° 003-2013-JUS):</>}
          </p>
          <ul className="t-small leading-loose list-none space-y-1 pl-1" style={{ color: c.textNormal }}>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? <>El <strong style={{ color: c.textPrimary }}>usuario</strong> (contador, contadora o empresa) actúa como <strong style={{ color: c.textPrimary }}>titular o responsable del tratamiento</strong> de los datos de sus clientes. Es su responsabilidad contar con la autorización expresa de dichos clientes antes de cargar sus datos en {APP_NAME}™.</>
                : <>The <strong style={{ color: c.textPrimary }}>user</strong> (accountant or company) acts as the <strong style={{ color: c.textPrimary }}>data controller</strong> for their clients' data. It is their responsibility to have the express authorization of such clients before uploading their data to {APP_NAME}™.</>}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? <><strong style={{ color: c.textPrimary }}>{LEGAL_NAME}</strong> actúa como <strong style={{ color: c.textPrimary }}>encargado del tratamiento</strong> de los datos ingresados, procesándolos únicamente con la finalidad de prestar el servicio contable contratado.</>
                : <><strong style={{ color: c.textPrimary }}>{LEGAL_NAME}</strong> acts as the <strong style={{ color: c.textPrimary }}>data processor</strong> of the entered data, processing it solely for the purpose of providing the contracted accounting service.</>}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? `${LEGAL_NAME} aplica medidas de seguridad técnicas y organizativas conforme al Art. 39 del Reglamento (cifrado AES-256, RLS por empresa, control de acceso por rol).`
                : `${LEGAL_NAME} applies technical and organizational security measures pursuant to Art. 39 of the Regulations (AES-256 encryption, company-level RLS, role-based access control).`}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? `El usuario garantiza que los datos cargados son lícitos y que cuenta con base legal suficiente para su tratamiento. ${LEGAL_NAME} no verifica ni valida este extremo.`
                : `The user warrants that uploaded data is lawful and that they have sufficient legal basis for its processing. ${LEGAL_NAME} does not verify or validate this.`}
            </li>
          </ul>
        </div>

        {/* 3 — Propiedad Intelectual / Intellectual Property */}
        <div className="rounded-2xl p-6 space-y-3" style={cardStyle}>
          <h2 className={SECTION_TITLE} style={{ color: c.textPrimary }}>
            <BookOpen size={15} className="text-cyan-400 shrink-0" />
            {isEs ? '3. Propiedad Intelectual' : '3. Intellectual Property'}
          </h2>
          <ul className="t-small leading-loose list-none space-y-1 pl-1" style={{ color: c.textNormal }}>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? <>El código fuente, la marca <strong style={{ color: c.textPrimary }}>TryQuiPus™</strong>, las interfaces de usuario, algoritmos contables, modelos de IA integrados y cualquier output generado automáticamente por la plataforma son propiedad exclusiva de {LEGAL_NAME} y están protegidos por la legislación de propiedad intelectual peruana e internacional.</>
                : <>The source code, the <strong style={{ color: c.textPrimary }}>TryQuiPus™</strong> brand, user interfaces, accounting algorithms, integrated AI models, and any output automatically generated by the platform are the exclusive property of {LEGAL_NAME} and are protected by Peruvian and international intellectual property law.</>}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? <>El usuario conserva la titularidad de sus <strong style={{ color: c.textPrimary }}>datos contables propios</strong> (facturas, asientos, balances y demás registros ingresados). {LEGAL_NAME} no reclama propiedad alguna sobre ellos.</>
                : <>The user retains ownership of their own <strong style={{ color: c.textPrimary }}>accounting data</strong> (invoices, entries, balance sheets, and other entered records). {LEGAL_NAME} claims no ownership over them.</>}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? `El usuario otorga a ${LEGAL_NAME} una licencia limitada, no exclusiva e intransferible para procesar dichos datos con el único fin de prestar el servicio contratado.`
                : `The user grants ${LEGAL_NAME} a limited, non-exclusive, non-transferable license to process such data solely for the purpose of providing the contracted service.`}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? `Queda prohibido copiar, descompilar, realizar ingeniería inversa, distribuir o crear obras derivadas del software de ${LEGAL_NAME} sin autorización escrita previa.`
                : `Copying, decompiling, reverse engineering, distributing, or creating derivative works of ${LEGAL_NAME} software without prior written authorization is prohibited.`}
            </li>
          </ul>
        </div>

        {/* 4 — Ley Aplicable / Applicable Law */}
        <div className="rounded-2xl p-6 space-y-3" style={cardStyle}>
          <h2 className={SECTION_TITLE} style={{ color: c.textPrimary }}>
            <Scale size={15} className="text-cyan-400 shrink-0" />
            {isEs ? '4. Ley Aplicable y Jurisdicción' : '4. Applicable Law and Jurisdiction'}
          </h2>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? <>Estos Términos se rigen íntegramente por la <strong style={{ color: c.textPrimary }}>legislación del Estado de New Jersey, EE.UU.</strong></>
              : <>These Terms are governed entirely by the <strong style={{ color: c.textPrimary }}>laws of the State of New Jersey, U.S.A.</strong></>}
          </p>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? <>Para cualquier controversia derivada del uso de {APP_NAME}™, las partes se someten a la competencia exclusiva de los <strong style={{ color: c.textPrimary }}>tribunales competentes del Estado de New Jersey, EE.UU.</strong>, renunciando expresamente a cualquier otro fuero que pudiera corresponderles.</>
              : <>For any dispute arising from the use of {APP_NAME}™, the parties submit to the exclusive jurisdiction of the <strong style={{ color: c.textPrimary }}>courts of the State of New Jersey, U.S.A.</strong>, expressly waiving any other jurisdiction that may apply.</>}
          </p>
          <div className="bg-cyan-400/5 border border-cyan-400/15 rounded-xl px-4 py-3">
            <p className="t-sm font-medium" style={{ color: c.info }}>
              {isEs ? 'Resolución alternativa' : 'Alternative resolution'}
            </p>
            <p className="t-2xs mt-1" style={{ color: c.textSub }}>
              {isEs
                ? 'Antes de iniciar cualquier acción judicial, ambas partes se comprometen a intentar resolver la disputa de buena fe mediante comunicación directa. Para disputas de menor cuantía se recomienda acudir a INDECOPI conforme al Código de Protección y Defensa del Consumidor (Ley N° 29571).'
                : 'Before initiating any legal action, both parties agree to attempt to resolve the dispute in good faith through direct communication. For minor claims, it is recommended to turn to INDECOPI pursuant to the Consumer Protection and Defense Code (Law N° 29571).'}
            </p>
          </div>
        </div>

        {/* 5 — Terminación / Termination */}
        <div className="rounded-2xl p-6 space-y-3" style={cardStyle}>
          <h2 className={SECTION_TITLE} style={{ color: c.textPrimary }}>
            <Power size={15} className="text-cyan-400 shrink-0" />
            {isEs ? '5. Terminación del Servicio' : '5. Termination of Service'}
          </h2>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? 'Cualquiera de las partes puede dar por terminada la relación contractual en los siguientes supuestos:'
              : 'Either party may terminate the contractual relationship in the following circumstances:'}
          </p>
          <ul className="t-small leading-loose list-none space-y-1 pl-1" style={{ color: c.textNormal }}>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              <strong style={{ color: c.textPrimary }}>
                {isEs ? `${LEGAL_NAME} puede suspender o cancelar` : `${LEGAL_NAME} may suspend or cancel`}
              </strong>{' '}
              {isEs
                ? 'el acceso por: (a) falta de pago con más de 7 días de atraso; (b) incumplimiento de estos Términos; (c) uso fraudulento, ilegal o que ponga en riesgo la seguridad del sistema.'
                : 'access for: (a) non-payment exceeding 7 days; (b) breach of these Terms; (c) fraudulent, illegal use or use that jeopardizes system security.'}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              <strong style={{ color: c.textPrimary }}>
                {isEs ? 'El usuario puede cancelar' : 'The user may cancel'}
              </strong>{' '}
              {isEs
                ? <>en cualquier momento escribiendo a{' '}<a href={`mailto:${CONTACT.EMAIL_SUPPORT}`} className="text-cyan-400 hover:underline">{CONTACT.EMAIL_SUPPORT}</a>, sin penalidad. El acceso permanece activo hasta el final del período ya pagado.</>
                : <>at any time by writing to{' '}<a href={`mailto:${CONTACT.EMAIL_SUPPORT}`} className="text-cyan-400 hover:underline">{CONTACT.EMAIL_SUPPORT}</a>, without penalty. Access remains active until the end of the already-paid period.</>}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? <>Tras la cancelación, los datos del usuario permanecerán disponibles para su exportación durante <strong style={{ color: c.textPrimary }}>30 días calendario</strong>. Pasado ese plazo, los datos pasarán a estado archivado conforme al plazo legal contable de 7 años (Art. 43 del Código Tributario).</>
                : <>After cancellation, user data will remain available for export for <strong style={{ color: c.textPrimary }}>30 calendar days</strong>. After that period, data will be archived pursuant to the 7-year accounting legal period (Art. 43 of the Tax Code).</>}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? 'Para solicitar la eliminación definitiva de datos fuera del período legal obligatorio, el usuario deberá presentar una solicitud justificada a'
                : 'To request permanent deletion of data outside the mandatory legal period, the user must submit a justified request to'}{' '}
              <a href={`mailto:${CONTACT.EMAIL_PRIVACY}`} className="text-cyan-400 hover:underline">
                {CONTACT.EMAIL_PRIVACY}
              </a>.
            </li>
          </ul>
        </div>

        {/* 6 — Garantía AS-IS / Warranty */}
        <div className="rounded-2xl p-6 space-y-3" style={cardStyle}>
          <h2 className={SECTION_TITLE} style={{ color: c.textPrimary }}>
            <AlertTriangle size={15} className="text-cyan-400 shrink-0" />
            {isEs ? '6. Garantía y Limitación de Responsabilidad' : '6. Warranty and Limitation of Liability'}
          </h2>
          <p className={BODY} style={{ color: c.textNormal }}>
            {isEs
              ? <>{LEGAL_NAME} presta el servicio <strong style={{ color: c.textPrimary }}>"tal cual"</strong> (<em>as-is</em>), sin garantía de disponibilidad ininterrumpida al 100%. {LEGAL_NAME} aplicará esfuerzos comercialmente razonables para mantener el servicio operativo, pero no garantiza la ausencia de interrupciones, errores o pérdida de datos derivada de fuerza mayor o fallos de infraestructura de terceros (servidores en EE.UU. con certificación SOC 2/ISO 27001).</>
              : <>{LEGAL_NAME} provides the service <strong style={{ color: c.textPrimary }}>"as-is"</strong>, with no guarantee of 100% uninterrupted availability. {LEGAL_NAME} will make commercially reasonable efforts to keep the service operational, but does not guarantee the absence of interruptions, errors, or data loss arising from force majeure or third-party infrastructure failures (U.S.-based servers with SOC 2/ISO 27001 certification).</>}
          </p>
          <p className={BODY} style={{ color: c.textNormal }}>
            <strong style={{ color: c.textPrimary }}>
              {isEs ? 'Compromiso de disponibilidad (SLA):' : 'Service Level Agreement (SLA):'}
            </strong>{' '}
            {isEs
              ? `${LEGAL_NAME} se compromete a un uptime mínimo de 99% mensual. En caso de caída mayor que supere ese umbral, el período de suscripción del usuario se extenderá proporcionalmente al tiempo de indisponibilidad excedido, previa verificación del incidente por parte del equipo técnico.`
              : `${LEGAL_NAME} commits to a minimum monthly uptime of 99%. In the event of an outage exceeding this threshold, the user's subscription period will be extended proportionally to the excess downtime, subject to verification by the technical team.`}
          </p>
          <ul className="t-small leading-loose list-none space-y-1 pl-1" style={{ color: c.textNormal }}>
            <li>
              <span style={{ color: c.warning }} className="mr-2 font-bold">!</span>
              {isEs
                ? <>{LEGAL_NAME} <strong style={{ color: c.textPrimary }}>no reemplaza la asesoría contable o legal profesional</strong>. El usuario es responsable de verificar y validar todas sus declaraciones y presentaciones ante SUNAT antes de su envío.</>
                : <>{LEGAL_NAME} <strong style={{ color: c.textPrimary }}>does not replace professional accounting or legal advice</strong>. The user is responsible for verifying and validating all their filings and submissions to SUNAT before sending.</>}
            </li>
            <li>
              <span style={{ color: c.warning }} className="mr-2 font-bold">!</span>
              {isEs
                ? `${LEGAL_NAME} no asume responsabilidad por multas, sanciones, intereses moratorios u otras consecuencias derivadas de errores en declaraciones tributarias, aun cuando dichos errores provengan de datos procesados en la plataforma.`
                : `${LEGAL_NAME} assumes no liability for fines, penalties, late interest, or other consequences arising from errors in tax returns, even when such errors originate from data processed on the platform.`}
            </li>
            <li>
              <span className="text-cyan-400 mr-2">·</span>
              {isEs
                ? `La responsabilidad máxima acumulada de ${LEGAL_NAME} frente al usuario por cualquier causa, en cualquier período de 12 meses, estará limitada al importe total de las cuotas de suscripción efectivamente pagadas durante los últimos 3 meses previos al evento que origina la reclamación.`
                : `${LEGAL_NAME}'s maximum cumulative liability to the user for any cause, in any 12-month period, will be limited to the total subscription fees actually paid during the 3 months prior to the event giving rise to the claim.`}
            </li>
          </ul>
        </div>

        {/* Footer */}
        <div className="pt-6 pb-4 text-center space-y-2" style={{ borderTop: `1px solid ${c.borderSoft}` }}>
          <p className="t-sm" style={{ color: c.textSub }}>
            <strong style={{ color: c.textNormal }}>{APP_NAME}™</strong>{' '}
            · {isEs ? 'Términos y Condiciones Versión 1.0' : 'Terms and Conditions Version 1.0'}
          </p>
          <p className="t-2xs" style={{ color: c.textMuted }}>
            {isEs
              ? 'Vigente desde mayo 2026 · Sujeto a actualización con aviso de 30 días.'
              : 'Effective since May 2026 · Subject to updates with 30 days\' notice.'}
          </p>
          <p className="t-2xs mt-1" style={{ color: c.textMuted }}>
            {isEs ? 'Consultas:' : 'Inquiries:'}{' '}
            <a href={`mailto:${CONTACT.EMAIL_SUPPORT}`} className="text-cyan-400 hover:underline">
              {CONTACT.EMAIL_SUPPORT}
            </a>
          </p>
          <p className="t-2xs mt-2" style={{ color: c.textMuted, opacity: 0.7 }}>
            {isEs
              ? 'Ley N° 29733 — Protección de Datos Personales · Ley N° 29571 — Defensa del Consumidor · Código Civil del Perú'
              : 'Law N° 29733 — Personal Data Protection · Law N° 29571 — Consumer Defense · Civil Code of Peru'}
          </p>
          <p className="t-2xs mt-1" style={{ color: c.textMuted, opacity: 0.7 }}>
            © 2026 {LEGAL_NAME}. Todos los derechos reservados. TryQuiPus™ es una plataforma operada por {LEGAL_NAME}. TryQuiPus™ es una marca comercial en proceso de registro.
          </p>
        </div>

      </div>
    </div>
  );
}
